import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { formatEuroIt } from "../lib/euro";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

function MoneyCell({ value, onChange, onCommit, testId, className }) {
  const [f, setF] = useState(false);
  return (
    <Input
      value={f ? (value ?? "") : formatEuroIt(value)}
      onFocus={() => setF(true)}
      onBlur={() => { setF(false); onCommit && onCommit(); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={className}
    />
  );
}

export default function ListinoSpeseDialog({ open, onOpenChange, onChanged }) {
  const [voci, setVoci] = useState([]);
  const [nuovo, setNuovo] = useState({ descrizione: "", importo: "" });
  const [savedId, setSavedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/studio/listino-spese");
      setVoci(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async () => {
    if (!nuovo.descrizione.trim()) { toast.error("Inserisci una descrizione"); return; }
    try {
      await api.post("/studio/listino-spese", nuovo);
      setNuovo({ descrizione: "", importo: "" });
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveRow = async (v) => {
    try {
      await api.put(`/studio/listino-spese/${v.id}`, { descrizione: v.descrizione, importo: v.importo });
      setSavedId(v.id);
      setTimeout(() => setSavedId((s) => (s === v.id ? null : s)), 1600);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (v) => {
    try { await api.delete(`/studio/listino-spese/${v.id}`); setVoci((x) => x.filter((i) => i.id !== v.id)); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!open) return null;
  const inp = "h-9 bg-slate-900/60 border-slate-700 text-slate-100";
  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-[6vh]" data-testid="listino-dialog">
      <div className="w-[80%] min-w-0 sm:min-w-[700px] max-w-[900px] max-h-[85vh] bg-[#0d111a] border border-slate-800 rounded-xl flex flex-col overflow-hidden quant-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div className="text-sm font-semibold text-slate-100">Gestisci Listino Voci di Spesa</div>
          <button onClick={() => onOpenChange(false)} data-testid="listino-close" className="text-slate-400 hover:text-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          <div className="grid grid-cols-[1fr_160px_auto] gap-2 items-end">
            <label className="block"><span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Descrizione</span>
              <Input value={nuovo.descrizione} onChange={(e) => setNuovo((n) => ({ ...n, descrizione: e.target.value }))} data-testid="listino-new-desc" className={inp} /></label>
            <label className="block"><span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Importo €</span>
              <Input value={nuovo.importo} onChange={(e) => setNuovo((n) => ({ ...n, importo: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") add(); }} onBlur={(e) => { const val = e.target.value; if (val.trim() !== "") setNuovo((n) => ({ ...n, importo: formatEuroIt(val) })); }} data-testid="listino-new-imp" className={inp} /></label>
            <Button onClick={add} data-testid="listino-add" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
          </div>
          <div className="rounded-lg border border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 text-xs uppercase bg-slate-900/60"><th className="px-3 py-2">Descrizione</th><th className="px-3 py-2 w-40">Importo €</th><th className="px-3 py-2 w-10" /></tr></thead>
              <tbody>
                {voci.length === 0 ? <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500" data-testid="listino-empty">Nessuna voce nel listino</td></tr>
                  : voci.map((v, i) => (
                    <tr key={v.id} className="border-t border-slate-800">
                      <td className="px-3 py-1.5"><Input value={v.descrizione} onChange={(e) => setVoci((x) => x.map((it, j) => j === i ? { ...it, descrizione: e.target.value } : it))} onBlur={() => saveRow(voci[i])} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} data-testid={`listino-desc-${i}`} className={inp} /></td>
                      <td className="px-3 py-1.5"><MoneyCell value={v.importo} onChange={(val) => setVoci((x) => x.map((it, j) => j === i ? { ...it, importo: val } : it))} onCommit={() => { const val = formatEuroIt(voci[i].importo); setVoci((x) => x.map((it, j) => j === i ? { ...it, importo: val } : it)); saveRow({ ...voci[i], importo: val }); }} testId={`listino-imp-${i}`} className={inp} /></td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          {savedId === v.id && <Check className="h-4 w-4 text-emerald-400" data-testid={`listino-saved-${i}`} />}
                          <button onClick={() => del(v)} data-testid={`listino-del-${i}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500">Le voci sono condivise tra tutte le pratiche dello studio. Importo mostrato: {formatEuroIt(1234.5)} €.</p>
        </div>
      </div>
    </div>
  );
}
